import { Group } from 'src/design/atoms/Group';
import { Stack } from 'src/design/atoms/Stack';
import { Textarea } from 'src/design/atoms/Textarea';
import React, { useEffect, useRef, useState } from 'react';
import { BasicButton } from 'src/design/atoms/BasicButton';
import { SecondaryButton } from 'src/design/atoms/SecondaryButton';

export interface IMessageEditorProps {
  initialText: string;
  onSubmit: (text: string) => void;
  onCancel: () => void;
}

/** @registry In-place editor for a sent chat question: autosize textarea opened focused with the caret at the end, Cancel and Send below; Enter sends, Shift+Enter adds a newline, Escape cancels, Send is disabled for blank text. */
export const MessageEditor: React.FC<IMessageEditorProps> = ({
  initialText,
  onSubmit,
  onCancel,
}) => {
  const [text, setText] = useState(initialText);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    const textarea = textareaRef.current;
    if (!textarea) return;
    textarea.focus();
    textarea.setSelectionRange(textarea.value.length, textarea.value.length);
  }, []);

  const submit = () => {
    if (text.trim()) onSubmit(text);
  };

  return (
    <Stack gap="xs">
      <Textarea
        ref={textareaRef}
        aria-label="Edit message"
        // The drawer closes on Escape from a window capture-phase listener, which runs before this
        // handler; the attribute is the only way to keep Escape from also closing the drawer.
        data-mantine-stop-propagation="true"
        autosize
        minRows={1}
        maxRows={10}
        value={text}
        onChange={(event) => setText(event.currentTarget.value)}
        onKeyDown={(event) => {
          if (event.key === 'Enter' && !event.shiftKey) {
            event.preventDefault();
            submit();
          } else if (event.key === 'Escape') {
            event.preventDefault();
            onCancel();
          }
        }}
      />
      <Group justify="flex-end" gap="xs">
        <SecondaryButton size="xs" onClick={onCancel}>
          Cancel
        </SecondaryButton>
        <BasicButton
          size="xs"
          variant="filled"
          disabled={!text.trim()}
          onClick={submit}
        >
          Send
        </BasicButton>
      </Group>
    </Stack>
  );
};
